import { FaTiktok, FaInstagram, FaTwitch } from "react-icons/fa";
import { useState } from 'react';
import { ArrowRight, PlugZap } from 'lucide-react';
import CaseStudySection from "./CaseStudySection";
import { trackStatsEvent } from '../utils/statsAnalytics';
import { statsPreview } from '../data/statsPreview';
const MINT = "#84ff00";
const PURPLE = "#9146FF";
const BG = "#07060d";
const HAIR = "rgba(255,255,255,0.08)";
const SURF = "rgba(255,255,255,0.035)";
const PLATFORMS = [{
  id: "tiktok",
  label: "TikTok"
}, {
  id: "instagram",
  label: "Instagram"
}, {
  id: "twitch",
  label: "Twitch"
}];
const fmt = n => n == null ? "—" : n.toLocaleString("en-US");
const estViews = likes => Math.round(likes / 0.06);

/* ---------------- shared bits ---------------- */

function Badge({
  children,
  tone = "purple"
}) {
  const c = tone === "mint" ? MINT : PURPLE;
  return <span className="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider" style={{
    borderColor: `${c}55`,
    color: tone === "mint" ? MINT : "#c9a8ff",
    backgroundColor: `${c}14`
  }}>
      {children}
    </span>;
}
function Panel({
  children,
  className = ""
}) {
  return <div className={`rounded-2xl border ${className}`} style={{
    borderColor: HAIR,
    backgroundColor: SURF
  }}>
      {children}
    </div>;
}
const PREVIEW = statsPreview.posts.map(p => ({
  title: p.title,
  tv: p.tiktok.views,
  tl: p.tiktok.likes,
  tc: p.tiktok.comments,
  il: p.instagram.likes,
  ic: p.instagram.comments
}));
function PublicPreview() {
  const [p, setP] = useState("tiktok");
  const isTT = p === "tiktok";
  const cols = isTT ? ["Views", "Likes", "Comments"] : ["Est. views", "Likes", "Comments"];
  return <Panel className="overflow-hidden text-left">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4" style={{
      borderColor: HAIR
    }}>
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-white">Find the patterns behind your next high-view post</h2>
          <Badge tone="mint">Real account data</Badge>
        </div>
        <div className="inline-flex rounded-lg border p-0.5" style={{
        borderColor: HAIR
      }}>
          {["tiktok", "instagram"].map(k => <button key={k} aria-pressed={p === k} onClick={() => setP(k)} className="min-h-9 rounded-md px-3 text-xs font-semibold" style={{
          backgroundColor: p === k ? "rgba(255,255,255,0.1)" : "transparent",
          color: p === k ? "#fff" : "rgba(255,255,255,0.55)"
        }}>
              {k === "tiktok" ? "TikTok" : "Instagram"}
            </button>)}
        </div>
      </div>
      <div className="px-5 pt-4 text-xs text-white/50">
        <a href={isTT ? statsPreview.tiktok.url : statsPreview.instagram.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-white/80 hover:text-white">{isTT ? "@kre8content" : "@kre8_content"}</a> · Three selected
        posts · Snapshot from 27 Oct 2026
      </div>
      <div className="p-5 pt-3">
        <div className="hidden grid-cols-[minmax(0,1fr)_repeat(3,88px)] gap-3 border-b pb-2 text-[11px] font-semibold uppercase tracking-wider text-white/40 sm:grid" style={{
        borderColor: HAIR
      }}>
          <span>Video</span>
          {cols.map(c => <span key={c} className="text-right">{c}</span>)}
        </div>
        <ul className="divide-y" style={{
        borderColor: HAIR
      }}>
          {PREVIEW.map(r => {
          const vals = isTT ? [r.tv, r.tl, r.tc] : [estViews(r.il), r.il, r.ic];
          return <li key={r.title} className="py-3 sm:grid sm:grid-cols-[1fr_repeat(3,88px)] sm:items-center sm:gap-3" style={{
            borderColor: HAIR
          }}>
                <p className="text-sm font-medium leading-snug text-white">{r.title}</p>
                <div className="mt-2 grid grid-cols-3 gap-2 sm:contents">
                  {vals.map((v, i) => <div key={i} className="sm:text-right">
                      <span className="block text-[10px] uppercase tracking-wider text-white/40 sm:hidden">{cols[i]}</span>
                      <span className={`text-sm tabular-nums ${i === 0 ? "font-semibold text-white" : "text-white/75"}`}>
                        {i === 0 && !isTT ? "~" : ""}
                        {fmt(v)}
                      </span>
                    </div>)}
                </div>
              </li>;
        })}
        </ul>
        <p className="mt-3 text-[11px] leading-relaxed text-white/45">
          {isTT ? "Views, likes and comments come from TikTok. Titles shortened for display. This preview does not update live." : "Likes and comments come from Instagram. Est. views are likes ÷ 6%, not measured views. Titles shortened for display. This preview does not update live."}
        </p>
      </div>
    </Panel>;
}

/* ---------------- logged out ---------------- */

const AVAILABLE = {
  tiktok: {
    short: "Use video performance signals to find formats worth making again.",
    points: ["Views, likes, comments, and shares", "Watch time, retention, and followers gained when available", "Sort videos to spot repeatable winners"]
  },
  instagram: {
    short: "Compare Reel engagement to see what deserves another version.",
    points: ["Reel likes and comments", "Estimated views and comment rates", "Est. views use likes ÷ 6%, not measured views"]
  },
  twitch: {
    short: "Use broadcast history to find streams worth turning into more content.",
    points: ["Broadcast dates, durations, and titles", "Filter the last 7, 30, 60, or 90 days", "Open saved broadcasts on Twitch"]
  }
};
const PLATFORM_LOGO = { tiktok: FaTiktok, instagram: FaInstagram, twitch: FaTwitch };
const PLATFORM_COLOR = {
  tiktok: "#ff2d6f",
  instagram: "#e1306c",
  twitch: PURPLE
};
function Eyebrow({
  children
}) {
  return <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{
    color: MINT
  }}>{children}</p>;
}
function PrimaryLink({
  href,
  children
}) {
  return <a href={href} onClick={() => trackStatsEvent("stats_signup_click")} className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition hover:brightness-110" style={{
    backgroundColor: PURPLE,
    boxShadow: `0 10px 40px -12px ${PURPLE}, inset 0 1px 0 rgba(255,255,255,0.2)`
  }}>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </a>;
}
function SecondaryLink({
  href,
  children
}) {
  return <a href={href} className="inline-flex min-h-12 items-center justify-center rounded-full border px-6 text-sm font-semibold text-white/85 transition hover:bg-white/5" style={{
    borderColor: HAIR
  }}>
      {children}
    </a>;
}
function Check({
  color = MINT
}) {
  return <svg viewBox="0 0 16 16" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden>
      <circle cx="8" cy="8" r="8" fill={`${color}22`} />
      <path d="M5 8.2l2 2 4-4.4" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;
}
function Hero() {
  return <section className="pt-14 text-center sm:pt-20">
      <a href="#available-data" className="inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border py-1 pl-1 pr-3 text-xs text-white/70 transition hover:text-white" style={{
      borderColor: HAIR,
      backgroundColor: SURF
    }}>
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black" style={{
        backgroundColor: MINT
      }}>7-day free trial</span>
        For streamers, creators, marketers, and campaign managers
      </a>
      <h1 className="mx-auto mt-6 max-w-3xl text-[2.5rem] font-semibold leading-[1.02] tracking-[-0.035em] text-white sm:text-6xl lg:text-7xl">
        Use your stats to grow{ " "}
        <span className="bg-clip-text text-transparent" style={{
        backgroundImage: `linear-gradient(90deg, #c9a8ff, ${PURPLE} 45%, ${MINT})`
      }}>
          more views.
        </span>
      </h1>
      <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
        Compare the signals behind your TikTok videos, Instagram Reels, and Twitch broadcasts. Use views, engagement,
        watch time, and retention to see what earns attention, then make more of it.
      </p>
      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
        <PrimaryLink href="/signup?next=%2Fstats">Start your 7-day free trial</PrimaryLink>
        <SecondaryLink href="#available-data">See what’s available</SecondaryLink>
      </div>
      <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed text-white/55">Card required. Free for 7 days, then $10 USD/month automatically unless you cancel before your trial ends. Cancel anytime, keep access until your trial or paid period ends.</p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-white/40">
        <span className="uppercase tracking-[0.18em]">Works with</span>
        {PLATFORMS.map(p => <span key={p.id} className="flex items-center gap-1.5 text-sm font-semibold text-white/60">
            <span className="h-2 w-2 rounded-full" style={{
          backgroundColor: PLATFORM_COLOR[p.id]
        }} />
            {p.label}
          </span>)}
      </div>
    </section>;
}
function ProductFrame() {
  return <section className="relative mx-auto mt-14 max-w-5xl sm:mt-16">
      <div aria-hidden className="absolute -inset-x-10 -top-10 bottom-0 -z-0 blur-3xl" style={{
      background: `radial-gradient(50% 50% at 50% 30%, ${PURPLE}40, transparent 70%)`
    }} />
      <div className="relative rounded-2xl border p-1.5 sm:p-2" style={{
      borderColor: "rgba(255,255,255,0.12)",
      backgroundColor: "rgba(255,255,255,0.03)",
      boxShadow: "0 40px 120px -40px rgba(0,0,0,0.8)"
    }}>
        <div className="flex items-center gap-1.5 px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="ml-3 hidden rounded-md px-3 py-0.5 text-[11px] text-white/40 sm:inline" style={{
          backgroundColor: "rgba(255,255,255,0.05)"
        }}>kre8content.com/stats</span>
        </div>
        <div className="rounded-xl" style={{
        backgroundColor: BG
      }}>
          <PublicPreview />
        </div>
      </div>
    </section>;
}
function ConnectAndData() {
  return <section id="available-data" className="scroll-mt-24 pt-24 sm:pt-32">
      <div className="mx-auto max-w-2xl text-center">
        <Eyebrow>From data to your next move</Eyebrow>
        <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">TikTok analytics, Instagram Reels insights, and Twitch stream stats in one tracker.</h2>
        <p className="mt-3 text-white/55">Use one social media analytics dashboard to compare performance signals and plan stronger content.</p>
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {PLATFORMS.map(p => {
          const Connection = "a";
          const Logo = PLATFORM_LOGO[p.id];
          return <div key={p.id} className="flex flex-col rounded-2xl border p-6 transition hover:border-white/20" style={{
        borderColor: HAIR,
        backgroundColor: SURF
      }}>
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg text-sm font-bold text-white" style={{
            backgroundColor: `${PLATFORM_COLOR[p.id]}26`,
            color: PLATFORM_COLOR[p.id]
          }}>
                <Logo aria-hidden="true" className="h-5 w-5" />
              </span>
              <h3 className="text-lg font-semibold text-white">{p.label}</h3>
            </div>
            <p className="mt-3 text-sm text-white/55">{AVAILABLE[p.id].short}</p>
            <ul className="mt-5 space-y-2.5 border-t pt-5 md:mb-6" style={{
          borderColor: HAIR
        }}>
              {AVAILABLE[p.id].points.map(pt => <li key={pt} className="flex gap-2.5 text-sm text-white/75"><Check />{pt}</li>)}
            </ul>
            <Connection href={`/login?next=${encodeURIComponent(`/stats?tab=${p.id}`)}`} onClick={() => trackStatsEvent("stats_connect_click", { platform: p.id })} className="mt-6 cursor-pointer inline-flex min-h-11 items-center justify-center gap-2 rounded-full border text-sm font-semibold text-white transition hover:bg-white/5 md:mt-auto" style={{
          borderColor: HAIR
        }}>
              <PlugZap className="h-4 w-4" /> Connect {p.label}
            </Connection>
          </div>; })}
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-white/45">
        Currently having data issues with Meta, will be resolved shortly. Please focus on TikTok for now.
      </p>
    </section>;
}
const PLATFORM_GUIDES = [
  {
    title: "TikTok analytics for creators",
    copy: "Compare video performance and find formats worth testing again.",
    href: "/tiktok-analytics",
    color: "#ff2d6f"
  },
  {
    title: "Instagram Reels analytics",
    copy: "Use Reel engagement signals to make the next creative sharper.",
    href: "/instagram-reels-analytics",
    color: MINT
  },
  {
    title: "Twitch stream analytics",
    copy: "Find broadcasts worth turning into your next clips and posts.",
    href: "/twitch-stream-analytics",
    color: PURPLE
  }
];
function PlatformGuides() {
  return <section className="pt-24 sm:pt-32 text-center">
      <div className="mx-auto max-w-2xl text-center">
        <Eyebrow>Explore by platform</Eyebrow>
        <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">Start with the analytics that match your content.</h2>
        <p className="mt-3 text-white/55">Learn what each platform can show, then connect the account you use to make better content decisions.</p>
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {PLATFORM_GUIDES.map(guide => <a key={guide.href} href={guide.href} className="group flex min-h-[230px] flex-col items-center justify-center rounded-2xl border p-6 text-center transition hover:border-white/25 hover:bg-white/[0.055]" style={{
        borderColor: HAIR,
        backgroundColor: SURF
      }}>
            <span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: guide.color }} />
            <h3 className="mt-5 text-lg font-semibold text-white">{guide.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/55">{guide.copy}</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 transition group-hover:text-white">Explore analytics <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
          </a>)}
      </div>
      <p className="mx-auto mt-7 max-w-2xl text-center text-sm leading-relaxed text-white/55">Not sure where to start? <a href="/how-to-use-social-media-analytics" className="font-semibold text-white underline decoration-white/30 underline-offset-4 transition hover:decoration-white">Learn how to use social media analytics to plan your next post.</a></p>
    </section>;
}
function FinalCTA() {
  return <section className="pt-24 sm:pt-32">
      <div className="relative overflow-hidden rounded-3xl border px-6 py-14 text-center sm:py-20" style={{
      borderColor: HAIR,
      background: `radial-gradient(60% 100% at 50% 0%, ${PURPLE}33, transparent 70%), ${SURF}`
    }}>
        <h2 className="mx-auto max-w-xl text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">
          Turn today’s stats into tomorrow’s views.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-white/55">Find the posts worth repeating, improve the next ones, and grow with a clearer content strategy. Your social media analytics dashboard is free to try for 7 days.</p>
        <div className="mt-8 flex justify-center"><PrimaryLink href="/signup?next=%2Fstats">Start your 7-day free trial</PrimaryLink></div>
      </div>
    </section>;
}
export default function StatsLanding() {
  return <>
      <Hero />
      <ProductFrame />
      <CaseStudySection />
      <ConnectAndData />
      <PlatformGuides />
      <FinalCTA />
    </>;
}
